// src/pages/ShopifyImportedOrders.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { apiGet } from "../../api/client";
import { A, pillClass } from "../../styles/appleClasses";
import { Button } from "../../components/Button";
import SectionShell, {
  type SectionTab,
} from "../../components/layout/SectionShell";
import { companyRoute } from "../../utils/routeHelpers";
import ShopifyBillingRequiredPanel from "../../components/shopify/ShopifyBillingRequiredPanel";
import type { ShopifyStatus } from "../../types/shopify";
import { isShopifyBillingRequired } from "../../types/shopify";

type OrderLink = {
  id: number;
  connection_id?: number | null;
  shopify_order_id: string;
  shopify_order_number?: string | null;
  sale_id?: number | null;
  invoice_id?: number | null;
  payment_id?: number | null;
  import_status: string;
  imported_at?: string | null;
};

const SHOPIFY_TABS: SectionTab[] = [
  { label: "Overview", to: "/commerce/shopify", end: true },
  { label: "Product Mapping", to: "/commerce/shopify/product-mapping" },
  { label: "Sync Issues", to: "/commerce/shopify/sync-issues" },
  { label: "Activity", to: "/commerce/shopify/diagnostics" },
];

function shortDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function titleize(value?: string | null) {
  if (!value) return "Unknown";
  return value
    .replace(/_/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function statusTone(status?: string | null) {
  const s = (status || "").toLowerCase();
  if (s === "imported") return "success";
  if (s.includes("review") || s.includes("waiting")) return "warning";
  if (s.includes("failed") || s.includes("error")) return "warning";
  return "neutral";
}

export default function ShopifyImportedOrders() {
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderLink[]>([]);
  const [shopifyStatus, setShopifyStatus] = useState<ShopifyStatus | null>(
    null,
  );

  const shopifyBillingRequired = isShopifyBillingRequired(shopifyStatus);

  const query = new URLSearchParams(location.search).get("q") || "";

  async function refresh() {
    setBusy(true);
    setErr(null);

    try {
      const status = await apiGet<ShopifyStatus>("/shopify/status");
      setShopifyStatus(status);

      if (isShopifyBillingRequired(status)) {
        setOrders([]);
        return;
      }

      const rows = await apiGet<OrderLink[]>("/shopify/order-links");
      setOrders(rows || []);
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
          e?.message ||
          "Failed to load imported Shopify orders.",
      );
    } finally {
      setLoading(false);
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter((o) =>
      [
        o.shopify_order_id,
        o.shopify_order_number,
        o.import_status,
        o.sale_id ? String(o.sale_id) : "",
        o.invoice_id ? String(o.invoice_id) : "",
      ]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [orders, query]);

  if (shopifyBillingRequired) {
    return (
      <SectionShell
        title="Shopify"
        tabs={SHOPIFY_TABS}
        tabsAriaLabel="Shopify sections"
        closePath="/commerce/shopify"
      >
        <main className="mx-auto w-full max-w-3xl">
          <ShopifyBillingRequiredPanel
            installedAppUrl={shopifyStatus?.installed_app_url}
            shopifyAdminAppUrl={shopifyStatus?.shopify_admin_app_url}
          />
        </main>
      </SectionShell>
    );
  }

  return (
    <SectionShell
      title="Shopify"
      tabs={SHOPIFY_TABS}
      tabsAriaLabel="Shopify sections"
      closePath="/commerce/shopify"
    >
      <main className="mx-auto w-full max-w-3xl">
        <div className={`${A.card} p-4 sm:p-5`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className={A.title}>Imported orders</h2>
              <p className={`${A.label} mt-1`}>
                Read-only Shopify order import history. This view does not
                re-import or change accounting records.
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={refresh}
              disabled={busy}
              minWidth="auto"
              className="px-3"
            >
              {busy ? "Refreshing…" : "Refresh"}
            </Button>
          </div>

          {err && (
            <div className="mt-3 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-600">
              {err}
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500">
            Showing {loading ? "—" : filtered.length} of{" "}
            {loading ? "—" : orders.length} order links.
          </div>

          <div className="mt-4 space-y-3">
            {filtered.map((order) => (
              <div
                key={order.id}
                className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-3"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="text-[13px] font-semibold text-gray-700">
                        {order.shopify_order_number || order.shopify_order_id}
                      </div>
                      <span
                        className={pillClass(statusTone(order.import_status))}
                      >
                        {titleize(order.import_status)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      Shopify order ID {order.shopify_order_id} · Imported{" "}
                      {shortDate(order.imported_at)}
                    </div>
                  </div>
                  <Link
                    to={companyRoute(
                      company_id,
                      `/commerce/shopify/sync-issues?q=${encodeURIComponent(order.shopify_order_id)}`,
                    )}
                    className="text-xs font-medium text-appleblue hover:text-appleblueD"
                  >
                    Related issues
                  </Link>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <div className="rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Sale</div>

                    {order.sale_id ? (
                      <Link
                        to={companyRoute(company_id, `/sales/${order.sale_id}`)}
                        className="mt-1 block text-[13px] font-medium text-appleblue hover:text-appleblueD"
                      >
                        #{order.sale_id}
                      </Link>
                    ) : (
                      <div className="mt-1 text-[13px] font-medium text-gray-700">
                        —
                      </div>
                    )}
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Invoice</div>
                    <div className="mt-1 text-[13px] font-medium text-gray-700">
                      {order.invoice_id ? `#${order.invoice_id}` : "—"}
                    </div>
                  </div>
                  <div className="rounded-xl border border-gray-100 bg-white px-3 py-2">
                    <div className={A.label}>Payment</div>
                    <div className="mt-1 text-[13px] font-medium text-gray-700">
                      {order.payment_id ? `#${order.payment_id}` : "—"}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {!loading && filtered.length === 0 ? (
              <div className="rounded-2xl border border-gray-100 bg-gray-50 px-4 py-6 text-center text-[13px] text-gray-500">
                No imported Shopify orders found.
              </div>
            ) : null}
          </div>
        </div>
      </main>
    </SectionShell>
  );
}
